import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useGetProductsQuery } from "../../../api/productApi";
import {
  useGetCategoriesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
} from "../../../api/productTaxonomyApi";
import {
  setCategory,
  setTag,
  setBrand,
  setPriceRange,
  setSorting,
  setPage,
  setPerPage,
  setSearch,
  setActiveView,
  setOnSale,
  setFilters,
  setPagination,
} from "../../../productSlice";
import { useProductsUrlSync } from "../../../hooks";
import {
  buildCategoryTree,
  hasCategoryProducts,
} from "../../../helpers/productsCategoryHelpers";
import FilterSidebar from "./FilterSidebar";
import ProductsGrid from "./ProductsGrid";
import CategoriesBarLarge from "./CategoriesBarLarge";
import CategoriesBarNarrow from "./CategoriesBarNarrow";
import RTKQErrorCard from "../../../../../../common/components/RTKQErrorCard";
import type { RootState, AppDispatch } from "../../../../../../app/store";
import type {
  FilterState,
  PaginationState,
  OrderByOptions,
} from "../../../productSlice";
import type {
  GetProductsParams,
  TransformedProduct,
} from "../../../types/productApiTypes";
import type {
  TransformedWPCategory,
  TransformedWPTag,
  TransformedWPBrand,
  WPBrandsById,
  WPCategoriesById,
  WPTagsById,
} from "../../../types/productTaxonomyApiTypes";

const ProductsContainer: React.FC = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch<AppDispatch>();
  const filters = useSelector((state: RootState) => state.product.filters);
  const pagination = useSelector(
    (state: RootState) => state.product.pagination
  );
  const activeView = useSelector(
    (state: RootState) => state.product.ui.activeView
  );

  const [isFilterOffcanvasOpen, setIsFilterOffcanvasOpen] = useState(false);

  const closeFilterOffcanvas = useCallback(() => {
    setIsFilterOffcanvasOpen(false);
  }, []);

  const toggleFilterOffcanvas = useCallback(() => {
    setIsFilterOffcanvasOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isFilterOffcanvasOpen) {
      document.body.classList.add("filter-offcanvas-open");
    } else {
      document.body.classList.remove("filter-offcanvas-open");
    }
    return () => {
      document.body.classList.remove("filter-offcanvas-open");
    };
  }, [isFilterOffcanvasOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeFilterOffcanvas();
      }
    };
    if (isFilterOffcanvasOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterOffcanvasOpen, closeFilterOffcanvas]);

  const memoizedSetFilters = useCallback(
    (filtersData: Partial<FilterState>) => {
      dispatch(setFilters(filtersData));
    },
    [dispatch]
  );

  const memoizedSetPagination = useCallback(
    (paginationData: Partial<PaginationState>) => {
      dispatch(setPagination(paginationData));
    },
    [dispatch]
  );

  const urlSyncComplete = useProductsUrlSync(
    filters,
    pagination,
    memoizedSetFilters,
    memoizedSetPagination
  );

  const queryParams: GetProductsParams = {
    category: filters.category.length > 0 ? filters.category : undefined,
    tag: filters.tag.length > 0 ? filters.tag : undefined,
    brand: filters.brand.length > 0 ? filters.brand : undefined,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    search: filters.search || undefined,
    orderby: filters.orderby,
    onSale: filters.onSale,
    page: pagination.currentPage,
    limit: pagination.perPage,
  };

  const cleanedQueryParams = Object.entries(queryParams).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key as keyof GetProductsParams] = value as any;
      }
      return acc;
    },
    {} as Partial<GetProductsParams>
  );

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isFetching,
    isError,
    error,
  } = useGetProductsQuery(cleanedQueryParams, {
    skip: !urlSyncComplete,
  });

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery();
  const { data: tagsData, isLoading: isTagsLoading } = useGetTagsQuery();
  const { data: brandsData, isLoading: isBrandsLoading } = useGetBrandsQuery();

  const categoriesById: WPCategoriesById | undefined =
    categoriesData?.categories;
  const categoriesArray: TransformedWPCategory[] = categoriesById
    ? Object.values(categoriesById)
    : [];
  const tagsById: WPTagsById | undefined = tagsData?.tags;
  const tagsArray: TransformedWPTag[] = tagsById ? Object.values(tagsById) : [];

  const brandsById: WPBrandsById | undefined = brandsData?.brands;
  const brandsArray: TransformedWPBrand[] = brandsById
    ? Object.values(brandsById)
    : [];

  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesArray),
    [categoriesArray]
  );

  const categoriesWithProductsTree = useMemo(
    () => categoryTree.filter(hasCategoryProducts),
    [categoryTree]
  );

  const handleCategoryClick = (categoryId: number | null) => {
    if (categoryId === null) {
      dispatch(setCategory({ action: "clear" }));
      return;
    }
    const isSelected = filters.category.includes(categoryId);
    if (isSelected) {
      dispatch(setCategory({ action: "clear" }));
    } else {
      dispatch(setCategory({ replace: [categoryId] }));
    }
  };

  const handleTagClick = (tagId: number) => {
    dispatch(setTag(tagId));
  };

  const handleBrandClick = (brandSlug: string) => {
    dispatch(setBrand(brandSlug));
  };

  const handlePriceFilter = ({
    minPrice,
    maxPrice,
  }: {
    minPrice: number | null;
    maxPrice: number | null;
  }) => {
    dispatch(setPriceRange({ minPrice, maxPrice }));
  };

  const handleSearchFilter = (searchQuery: string) => {
    dispatch(setSearch(searchQuery));
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value as OrderByOptions;
    dispatch(setSorting(selectedValue));
  };

  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
  };

  const handlePerPageChange = (perPage: number) => {
    dispatch(setPerPage(perPage));
  };

  const handleViewChange = (view: "grid" | "list") => {
    dispatch(setActiveView(view));
  };

  const handleOnSaleToggle = (checked: boolean) => {
    dispatch(setOnSale(checked));
  };

  const products: TransformedProduct[] = productsData?.products || [];
  const paginationInfo = productsData?.pagination || {
    totalProducts: 0,
    totalPages: 0,
  };
  const paginationData = {
    currentPage: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: paginationInfo.totalPages,
    totalProducts: paginationInfo.totalProducts,
  };

  const isProductsGridLoading = isProductsLoading || !urlSyncComplete;
  const isFilterSidebarLoading =
    !urlSyncComplete || isCategoriesLoading || isTagsLoading || isBrandsLoading;

  return (
    <div className="products-container">
      <div
        className={`filter-offcanvas-overlay ${
          isFilterOffcanvasOpen ? "is-open" : ""
        }`}
        onClick={closeFilterOffcanvas}
        aria-hidden={!isFilterOffcanvasOpen}
        tabIndex={-1}
      />
      <div
        className={`filter-sidebar-offcanvas-wrapper ${
          isFilterOffcanvasOpen ? "is-open" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-filter-offcanvas-title"
        hidden={!isFilterOffcanvasOpen}
      >
        <FilterSidebar
          idPrefix="mobile"
          categories={categoriesArray}
          tags={tagsArray}
          brands={brandsArray}
          activeFilters={filters}
          onCategoryClick={handleCategoryClick}
          onTagClick={handleTagClick}
          onBrandClick={handleBrandClick}
          onPriceFilter={handlePriceFilter}
          onSearchFilter={handleSearchFilter}
          onSaleToggle={handleOnSaleToggle}
          isLoading={isFilterSidebarLoading}
          onClose={closeFilterOffcanvas}
        />
      </div>

      <div className="category-navigation-bars">
        <CategoriesBarLarge
          categories={categoriesWithProductsTree}
          activeCategoryIds={filters.category}
          onCategoryClick={handleCategoryClick}
          isLoading={isCategoriesLoading}
        />
        <CategoriesBarNarrow
          categories={categoriesWithProductsTree}
          activeCategoryIds={filters.category}
          onCategoryClick={handleCategoryClick}
          isLoading={isCategoriesLoading}
        />
      </div>

      <div className="products-archive-layout">
        <div className="filter-sidebar-desktop-wrapper">
          <FilterSidebar
            idPrefix="desktop"
            categories={categoriesArray}
            tags={tagsArray}
            brands={brandsArray}
            activeFilters={filters}
            onCategoryClick={handleCategoryClick}
            onTagClick={handleTagClick}
            onBrandClick={handleBrandClick}
            onPriceFilter={handlePriceFilter}
            onSearchFilter={handleSearchFilter}
            onSaleToggle={handleOnSaleToggle}
            isLoading={isFilterSidebarLoading}
          />
        </div>

        <div className="products-grid-column">
          <ProductsGrid
            products={products}
            isLoading={isProductsGridLoading}
            isFetching={isFetching}
            isError={isError}
            error={error}
            pagination={paginationData}
            filters={filters}
            activeView={activeView}
            onViewChange={handleViewChange}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onFilterToggle={toggleFilterOffcanvas}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductsContainer;
